import React from "react";
import {
  Table,
  Button,
  Badge,
  Spinner,
  Form,
  OverlayTrigger,
  Tooltip,
  Modal,
} from "react-bootstrap";
import { FaPlay, FaStop, FaEdit, FaInfoCircle } from "react-icons/fa";

const VmTable = ({
  vms,
  accIdx,
  onStart,
  onStop,
  onEditSchedule,
  getRowStyle,
  getBadgeVariant,
  getCronTooltipContent,
  getCronSummary,
  page,
  perPage,
  totalPages,
  onPageChange,
  onPerPageChange,
  onInfoClick,
  loadingHistory,
  historyData,
  showHistoryModal,
  onHistoryClose,
  scheduleLoading,
}) => {
  const isVmLoading = (vmIdx) => {
    const loadingKey = `${accIdx}-${vmIdx}`;
    return loadingHistory[loadingKey] || false;
  };

  const isScheduleLoading = (vmIdx, field) => {
    const loadingKey = `${accIdx}-${vmIdx}-${field}`;
    return scheduleLoading?.[loadingKey] || false;
  };

  return (
    <>
      <div className="d-flex justify-content-end mb-2">
        <Form.Select
          value={perPage}
          onChange={onPerPageChange}
          size="sm"
          style={{ width: "auto" }}
        >
          {[5, 10, 15, 20].map((num) => (
            <option key={num} value={num}>
              {num} per page
            </option>
          ))}
        </Form.Select>
      </div>

      <Table bordered hover responsive className="align-middle mb-0">
        <thead className="table-dark">
          <tr>
            <th style={{ width: "240px" }}>Name</th>
            <th className="text-center" style={{ width: "85px" }}>
              Status
            </th>
            <th style={{ width: "200px" }}>Start Schedule</th>
            <th style={{ width: "200px" }}>Stop Schedule</th>
            <th className="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {vms.map((vm, vmIdx) => (
            <tr key={vm.id || vmIdx} style={getRowStyle(vm.status)}>
              <td>
                <div className="d-flex flex-column align-items-start">
                  <div className="d-flex justify-content-between w-100">
                    <span className="text-start">{vm.name}</span>
                    {vm.isLoading && <Spinner animation="border" size="sm" />}
                  </div>
                  <small className="text-muted">ID: {vm.id}</small>
                  <small className="text-muted">Tag: {vm.tag}</small>
                  {vm.type !== "-" && (
                    <small className="text-muted">Type: {vm.type}</small>
                  )}

                </div>
              </td>
              <td className="text-end">
                <Badge bg={getBadgeVariant(vm.status)} pill>
                  {vm.status}
                </Badge>
              </td>
              <td>
                <div className="d-flex align-items-center gap-2">
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        {vm.startSchedule
                          ? `${vm.startSchedule}${vm.startScheduleTimezone ? ` (${vm.startScheduleTimezone})` : ""}`
                          : "No schedule set"}
                      </Tooltip>
                    }
                  >
                    <span>
                      {vm.startSchedule && vm.startSchedule !== "-"
                        ? getCronSummary(
                            vm.startSchedule,
                            vm.startScheduleTimezone,
                          )
                        : "—"}
                    </span>
                  </OverlayTrigger>
                  <span
                    style={{
                      display: "inline-flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "20px",
                      height: "20px",
                    }}
                  >
                    {isScheduleLoading(vmIdx, "startSchedule") ? (
                      <Spinner
                        animation="border"
                        size="sm"
                        style={{ width: "16px", height: "16px" }}
                      />
                    ) : (
                      <FaEdit
                        style={{ cursor: "pointer", fontSize: "16px" }}
                        onClick={() => onEditSchedule("startSchedule", vmIdx)}
                      />
                    )}
                  </span>
                </div>
              </td>
              <td>
                <div className="d-flex align-items-center gap-2">
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        {vm.stopSchedule
                          ? `${vm.stopSchedule}${vm.stopScheduleTimezone ? ` (${vm.stopScheduleTimezone})` : ""}`
                          : "No schedule set"}
                      </Tooltip>
                    }
                  >
                    <span>
                      {vm.stopSchedule && vm.stopSchedule !== "-"
                        ? getCronSummary(
                            vm.stopSchedule,
                            vm.stopScheduleTimezone,
                          )
                        : "—"}
                    </span>
                  </OverlayTrigger>
                  <span
                    style={{
                      display: "inline-flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "20px",
                      height: "20px",
                    }}
                  >
                    {isScheduleLoading(vmIdx, "stopSchedule") ? (
                      <Spinner
                        animation="border"
                        size="sm"
                        style={{ width: "16px", height: "16px" }}
                      />
                    ) : (
                      <FaEdit
                        style={{ cursor: "pointer", fontSize: "16px" }}
                        onClick={() => onEditSchedule("stopSchedule", vmIdx)}
                      />
                    )}
                  </span>
                </div>
              </td>
              <td className="text-center">
                <div className="d-inline-flex flex-wrap gap-1">
                  <Button
                    variant="outline-info"
                    size="sm"
                    className="me-1"
                    onClick={() => onInfoClick(vmIdx)}
                    disabled={isVmLoading(vmIdx)}
                  >
                    {isVmLoading(vmIdx) ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FaInfoCircle />
                    )}
                  </Button>
                  {vm.status === "Stopped" && (
                    <Button
                      variant="outline-success"
                      size="sm"
                      className="me-1"
                      onClick={() => onStart(vmIdx)}
                      disabled={vm.disableActions}
                    >
                      <FaPlay />
                    </Button>
                  )}
                  {(vm.status === "Running" || vm.status === "Available") && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => onStop(vmIdx)}
                      disabled={vm.disableActions}
                    >
                      <FaStop />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {totalPages > 1 && (
        <div className="d-flex justify-content-end mt-2">
          <Form.Select
            value={page}
            size="sm"
            style={{ width: "auto" }}
            onChange={onPageChange}
          >
            {Array.from({ length: totalPages }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                Page {i + 1}
              </option>
            ))}
          </Form.Select>
        </div>
      )}

      {/* History Modal */}
      <Modal show={showHistoryModal} onHide={onHistoryClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Action History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {Object.values(loadingHistory).some((loading) => loading) ? (
            <div className="text-center my-3">
              <Spinner animation="border" />
            </div>
          ) : historyData.length === 0 ? (
            <div className="text-center my-3 text-muted">
              No logs available for this VM
            </div>
          ) : (
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th>User Name</th>
                  <th>Event Type</th>
                  <th>Action</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {historyData.map((rec, idx) => {
                  let eventType = "";
                  let cleanUser = rec.user;

                  if (rec.user.endsWith("-scheduler-session")) {
                    eventType = "User triggered";
                    cleanUser = rec.user.replace("-scheduler-session", "");
                  } else if (rec.user.endsWith("-cron-scheduler-event")) {
                    eventType = "Cron operation";
                    cleanUser = rec.user.replace("-cron-scheduler-event", "");
                  } else {
                    eventType = "Unknown";
                  }

                  return (
                    <tr key={idx}>
                      <td>{cleanUser}</td>
                      <td>{eventType}</td>
                      <td>{rec.action}</td>
                      <td>{rec.time}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHistoryClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VmTable;
